import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import { Badge } from '../../components/ui/badge';
import { ScrollArea } from '../../components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Mail, MailOpen, MousePointerClick, CheckCircle2, Bell, RefreshCw, DollarSign, Search, Users, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { pollEmailJob } from '../../lib/emailJobs';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STAGE_FILTERS = [
  { value: 'all', label: 'All Pre-Orders' },
  { value: 'not_emailed', label: 'Not Emailed Yet' },
  { value: 'not_opened', label: 'Emailed, Not Opened' },
  { value: 'opened_not_selected', label: 'Opened, No Selections' },
  { value: 'selected_not_approved', label: 'Selected, Not Approved' },
  { value: 'approved', label: 'Approved' },
];

const fmtDate = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  return d.toLocaleString('en-CA', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true });
};

const stageOf = (row) => {
  if (row.approved) return 'approved';
  if (row.selected_at) return 'selected_not_approved';
  if (row.opened_at) return 'opened_not_selected';
  if (row.emailed_at) return 'not_opened';
  return 'not_emailed';
};

const StatCard = ({ icon: Icon, label, value, sub, color, testId }) => (
  <Card className="border-stone-200" data-testid={testId}>
    <CardContent className="py-4 px-5">
      <div className="flex items-center gap-3">
        <div className={`h-10 w-10 rounded-full flex items-center justify-center ${color}`}>
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <p className="text-2xl font-bold text-stone-800">{value}</p>
          <p className="text-xs text-stone-500">{label}{sub ? ` · ${sub}` : ''}</p>
        </div>
      </div>
    </CardContent>
  </Card>
);

const EmailResponsesPage = () => {
  const [data, setData] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [eventFilter, setEventFilter] = useState('all');
  const [stageFilter, setStageFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState([]);
  const [showReminderDialog, setShowReminderDialog] = useState(false);
  const [reminderTemplate, setReminderTemplate] = useState({ subject: '', message: '' });
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [sending, setSending] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });

  const fetchData = async (eventId = eventFilter) => {
    try {
      const params = eventId !== 'all' ? `?event_id=${eventId}` : '';
      const res = await axios.get(`${API}/admin/response-dashboard${params}`);
      setData(res.data);
    } catch (error) {
      toast.error('Failed to load response data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    setSelected([]);
  }, [eventFilter]);

  useEffect(() => {
    axios.get(`${API}/preorder-events`).then(res => setEvents(res.data)).catch(() => {});
    axios.get(`${API}/reminder-template`).then(res => setReminderTemplate(res.data)).catch(() => {});
  }, []);

  const rows = useMemo(() => {
    if (!data) return [];
    let r = data.preorders;
    if (stageFilter !== 'all') r = r.filter(row => stageOf(row) === stageFilter);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      r = r.filter(row =>
        (row.child_name || '').toLowerCase().includes(q) ||
        (row.parent_name || '').toLowerCase().includes(q) ||
        (row.team_name || '').toLowerCase().includes(q) ||
        (row.email || '').toLowerCase().includes(q)
      );
    }
    return r;
  }, [data, stageFilter, search]);

  const remindable = (row) => Boolean(row.email) && !row.approved;
  const remindableRows = rows.filter(remindable);

  const toggleRow = (id) => setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const selectAllVisible = () => {
    const ids = remindableRows.map(r => r.id);
    setSelected(prev => prev.length === ids.length ? [] : ids);
  };

  const selectStragglers = () => {
    if (!data) return;
    const ids = data.preorders.filter(r => remindable(r) && r.emailed_at && !r.opened_at).map(r => r.id);
    if (ids.length === 0) {
      toast.info('No stragglers found - everyone who was emailed has opened their link!');
      return;
    }
    setStageFilter('not_opened');
    setSelected(ids);
    toast.success(`Selected ${ids.length} client${ids.length === 1 ? '' : 's'} who haven't opened their email`);
  };

  const saveTemplate = async () => {
    setSavingTemplate(true);
    try {
      await axios.put(`${API}/reminder-template`, reminderTemplate);
      toast.success('Reminder template saved as default');
    } catch (error) {
      toast.error('Failed to save template');
    } finally {
      setSavingTemplate(false);
    }
  };

  const sendReminders = async () => {
    setSending(true);
    setProgress({ current: 0, total: selected.length });
    try {
      // Server runs large batches as a background job - no request timeouts
      const res = await axios.post(`${API}/admin/response-dashboard/send-reminders`, {
        preorder_ids: selected,
        base_url: window.location.origin,
        subject: reminderTemplate.subject,
        message: reminderTemplate.message,
      }, { timeout: 60000 });
      let agg;
      if (res.data.status === 'queued') {
        agg = await pollEmailJob(res.data.job_id, (j) => {
          setProgress({ current: j.processed, total: j.total });
        });
      } else {
        agg = res.data;
      }
      toast.success(`Sent ${agg.sent.length} reminder${agg.sent.length === 1 ? '' : 's'}!`);
      if (agg.failed.length > 0) toast.error(`${agg.failed.length} failed to send`);
      if (agg.skipped.length > 0) toast.warning(`${agg.skipped.length} skipped (${agg.skipped.map(s => s.reason).filter((v, i, a) => a.indexOf(v) === i).join(', ')})`);
      setSelected([]);
      setShowReminderDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reminders');
    } finally {
      setSending(false);
      setProgress({ current: 0, total: 0 });
    }
  };

  const sendSingleReminder = async (row) => {
    try {
      const res = await axios.post(`${API}/admin/response-dashboard/send-reminders`, {
        preorder_ids: [row.id],
        base_url: window.location.origin,
        force: true,
      }, { timeout: 30000 });
      if (res.data.sent.length > 0) {
        toast.success(`Reminder sent to ${row.parent_name || row.email}`);
        fetchData();
      } else {
        const reason = res.data.skipped[0]?.reason || res.data.failed[0]?.reason || 'Unknown';
        toast.error(`Could not send: ${reason}`);
      }
    } catch (error) {
      toast.error('Failed to send reminder');
    }
  };

  const stats = data?.stats;
  const gStats = data?.gallery_stats;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading text-stone-800">Response Dashboard</h1>
          <p className="text-stone-600 mt-1">Track who's opened, selected, and approved - and nudge the stragglers</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={() => fetchData()} data-testid="refresh-responses-btn">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button variant="outline" onClick={selectStragglers} data-testid="select-stragglers-btn">
            <AlertCircle className="h-4 w-4 mr-2" />
            Select Stragglers
          </Button>
          <Button
            className="bg-amber-700 hover:bg-amber-800"
            disabled={selected.length === 0}
            onClick={() => setShowReminderDialog(true)}
            data-testid="send-reminders-btn"
          >
            <Bell className="h-4 w-4 mr-2" />
            Send Reminders ({selected.length})
          </Button>
        </div>
      </div>

      {/* Funnel Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <StatCard icon={Users} label="Pre-Orders" value={stats.total} color="bg-stone-100 text-stone-600" testId="stat-total" />
          <StatCard icon={Mail} label="Emailed" value={stats.emailed} color="bg-blue-100 text-blue-600" testId="stat-emailed" />
          <StatCard icon={MailOpen} label="Opened Link" value={stats.opened} color="bg-indigo-100 text-indigo-600" testId="stat-opened" />
          <StatCard icon={MousePointerClick} label="Selected Photos" value={stats.selected} color="bg-purple-100 text-purple-600" testId="stat-selected" />
          <StatCard icon={CheckCircle2} label="Approved" value={stats.approved} color="bg-green-100 text-green-600" testId="stat-approved" />
          <StatCard icon={AlertCircle} label="No Response" value={stats.no_response} sub="emailed, not opened" color="bg-red-100 text-red-600" testId="stat-no-response" />
        </div>
      )}

      {/* Gallery summary strip */}
      {gStats && gStats.total > 0 && (
        <Card className="border-stone-200 bg-stone-50">
          <CardContent className="py-3 px-5">
            <div className="flex items-center gap-6 flex-wrap text-sm text-stone-600">
              <span className="font-medium text-stone-700">Galleries:</span>
              <span data-testid="gallery-stat-total">{gStats.total} total</span>
              <span>{gStats.published} published</span>
              <span>{gStats.emailed} emailed</span>
              <span>{gStats.viewed} viewed</span>
              <span className="text-green-700 font-medium">{gStats.ordered} with orders</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters + Table */}
      <Card className="border-stone-200">
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <CardTitle className="font-heading text-xl text-stone-800">Client Responses</CardTitle>
              <CardDescription>Select clients and send them a one-click reminder with their review link</CardDescription>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <div className="relative">
                <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
                <Input
                  placeholder="Search name, team, email..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9 w-56"
                  data-testid="response-search-input"
                />
              </div>
              <Select value={eventFilter} onValueChange={setEventFilter}>
                <SelectTrigger className="w-[190px]" data-testid="response-event-filter">
                  <SelectValue placeholder="All Events" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Events</SelectItem>
                  {events.map(ev => (
                    <SelectItem key={ev.id} value={ev.id}>{ev.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={stageFilter} onValueChange={(v) => { setStageFilter(v); setSelected([]); }}>
                <SelectTrigger className="w-[210px]" data-testid="response-stage-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STAGE_FILTERS.map(f => (
                    <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button variant="outline" size="sm" onClick={selectAllVisible} data-testid="select-all-visible-btn">
                {selected.length === remindableRows.length && remindableRows.length > 0 ? 'Deselect All' : 'Select All'}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-3">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="h-14 bg-stone-100 rounded animate-pulse" />
              ))}
            </div>
          ) : rows.length > 0 ? (
            <ScrollArea className="h-[520px] pr-4">
              <div className="space-y-2">
                {rows.map((row) => {
                  const canRemind = remindable(row);
                  return (
                    <div
                      key={row.id}
                      className={`flex items-center gap-3 p-3 rounded-sm border transition-colors ${
                        selected.includes(row.id)
                          ? 'bg-amber-50 border-amber-300'
                          : 'bg-white border-stone-200 hover:border-stone-300'
                      }`}
                      data-testid={`response-row-${row.id}`}
                    >
                      <Checkbox
                        checked={selected.includes(row.id)}
                        disabled={!canRemind}
                        onCheckedChange={() => canRemind && toggleRow(row.id)}
                        data-testid={`response-checkbox-${row.id}`}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-stone-800 truncate">
                          {row.child_name} {row.team_name ? <span className="text-stone-400 font-normal">({row.team_name})</span> : null}
                        </p>
                        <p className="text-sm text-stone-500 truncate">
                          {row.parent_name || 'Parent'} • {row.email || 'No email'}
                        </p>
                      </div>
                      <div className="hidden md:flex items-center gap-1.5 flex-wrap justify-end max-w-[420px]">
                        <Badge className={row.emailed_at ? 'bg-blue-100 text-blue-700' : 'bg-stone-100 text-stone-400'} title={fmtDate(row.emailed_at) || ''}>
                          <Mail className="h-3 w-3 mr-1" />{row.emailed_at ? fmtDate(row.emailed_at) : 'Not emailed'}
                        </Badge>
                        <Badge className={row.opened_at ? 'bg-indigo-100 text-indigo-700' : 'bg-stone-100 text-stone-400'}>
                          <MailOpen className="h-3 w-3 mr-1" />{row.opened_at ? fmtDate(row.opened_at) : 'Not opened'}
                        </Badge>
                        <Badge className={row.selected_at ? 'bg-purple-100 text-purple-700' : 'bg-stone-100 text-stone-400'}>
                          <MousePointerClick className="h-3 w-3 mr-1" />{row.selected_at ? 'Selected' : 'No picks'}
                        </Badge>
                        <Badge className={row.approved ? 'bg-green-100 text-green-700' : 'bg-stone-100 text-stone-400'}>
                          <CheckCircle2 className="h-3 w-3 mr-1" />{row.approved ? 'Approved' : 'Pending'}
                        </Badge>
                        <Badge className={row.payment_status === 'paid' ? 'bg-emerald-100 text-emerald-700' : 'bg-orange-100 text-orange-600'}>
                          <DollarSign className="h-3 w-3 mr-0.5" />{row.payment_status === 'paid' ? 'Paid' : 'Unpaid'}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-2">
                        {row.reminder_sent_at && (
                          <span className="text-xs text-stone-400 hidden sm:block" title={`Reminder sent ${fmtDate(row.reminder_sent_at)}`}>
                            <Bell className="h-3 w-3 inline mr-0.5" />{fmtDate(row.reminder_sent_at)}
                          </span>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={!canRemind}
                          onClick={() => sendSingleReminder(row)}
                          data-testid={`remind-btn-${row.id}`}
                        >
                          <Bell className="h-3.5 w-3.5 mr-1.5" />
                          Remind
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </ScrollArea>
          ) : (
            <div className="text-center py-12">
              <Mail className="h-12 w-12 text-stone-300 mx-auto mb-4" />
              <p className="text-stone-500">No pre-orders match these filters</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Reminder Dialog */}
      <Dialog open={showReminderDialog} onOpenChange={(open) => { if (!sending) setShowReminderDialog(open); }}>
        <DialogContent className="max-w-lg" data-testid="reminder-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Send Reminder Emails</DialogTitle>
            <DialogDescription>
              {selected.length} client{selected.length === 1 ? '' : 's'} will receive a reminder with their personal review link.
              Anyone already reminded in the last 24 hours will be skipped automatically.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="reminder-subject">Subject</Label>
              <Input
                id="reminder-subject"
                value={reminderTemplate.subject}
                onChange={(e) => setReminderTemplate({ ...reminderTemplate, subject: e.target.value })}
                data-testid="reminder-subject-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="reminder-message">Message</Label>
              <Textarea
                id="reminder-message"
                rows={7}
                className="font-mono text-sm"
                value={reminderTemplate.message}
                onChange={(e) => setReminderTemplate({ ...reminderTemplate, message: e.target.value })}
                data-testid="reminder-message-input"
              />
              <p className="text-xs text-stone-500">
                Variables: {'{parent_name}'}, {'{child_name}'}. The "Review Your Photos" button and link are added automatically.
              </p>
              <div className="flex justify-end">
                <Button variant="ghost" size="sm" onClick={saveTemplate} disabled={savingTemplate} data-testid="save-reminder-template-btn">
                  {savingTemplate ? 'Saving...' : 'Save as Default'}
                </Button>
              </div>
            </div>
          </div>
          {sending && (
            <div className="space-y-2">
              <div className="w-full bg-stone-100 rounded-full h-2">
                <div className="bg-amber-600 h-2 rounded-full transition-all"
                  style={{ width: `${progress.total ? (progress.current / progress.total) * 100 : 0}%` }} />
              </div>
              <p className="text-sm text-stone-500 text-center">
                Sending {progress.current} / {progress.total}... emails are sent from the server, so it's safe to close this page
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReminderDialog(false)} disabled={sending}>
              Cancel
            </Button>
            <Button onClick={sendReminders} disabled={sending} className="bg-amber-700 hover:bg-amber-800" data-testid="confirm-send-reminders-btn">
              <Bell className="h-4 w-4 mr-2" />
              {sending ? 'Sending...' : `Send ${selected.length} Reminder${selected.length === 1 ? '' : 's'}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmailResponsesPage;
